import React, { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { has, get, pick, isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { decode } from 'he'

import { DateTimeDisplay, PageDescription } from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'

import { ApplicationsTableContext } from './index'
import {
  exportDetailReport,
  getDetailReport,
} from '../../../../actions/detailReport'
import { State } from '../../../../reducers'
import { pruneObject } from '../../../../utils/objectManagement'

import { ApplicationDetail } from '../../../../typings/detailReport'
import { ReportStatus } from '../../../../typings/report'
import { DetailReportStore } from '../../../../typings/stores/DetailReportStore'

import styles from '../styles.module.css'

const ApplicationsTable = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    filters,
    pagination: { size, page },
    sort,
    changePageSize,
    changePage,
    changeSort,
  } = useContext(ApplicationsTableContext)

  const report = useSelector<State, DetailReportStore>(
    state => state.detailReport
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.detailReport.export, ['hasData', 'status', 'error']),
    loading: state.detailReport.export.status === 'fetching',
    hasError: !isEmpty(state.detailReport.export.error),
  }))

  useEffect(() => {
    dispatch(
      getDetailReport('applications', {
        ...filters,
        ...pruneObject(sort),
        size,
        page,
      })
    )
  }, [sort, page, size])

  const backUrl = has(location, 'state.performanceReportUrl')
    ? location.state.performanceReportUrl
    : '/app/reports'

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={backUrl} className={styles.backLink}>
            Back to Performance Report
          </Link>
          <h2>Applications Details</h2>
        </div>
        <ReportActions
          className={styles.export}
          exportStatus={exportStatus}
          reportStatus={exportStatus}
          fetchExport={() =>
            dispatch(
              exportDetailReport('applications', {
                ...filters,
                ...pruneObject(sort),
              })
            )
          }
        />
      </div>

      <PageDescription content="This report contains transactional application details for the data selected in the performance report." />

      <Table<ApplicationDetail[]>
        cols={[
          {
            text: 'Transaction ID',
            field: 'transactionId',
          },
          {
            text: 'Click Date',
            sortable: true,
            field: 'clickDate',
            render: row =>
              row.clickDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.clickDate)} />
              ),
          },
          {
            text: 'Product ID',
            sortable: true,
            field: 'productId',
          },
          {
            text: 'Product',
            sortable: true,
            field: 'productName',
            render: row => row.productName && decode(row.productName),
          },
          {
            text: 'Advertiser',
            field: 'advertiserName',
          },
          {
            text: 'Application Submission Date',
            sortable: true,
            field: 'applicationDate',
            render: row =>
              row.applicationDate && (
                <DateTimeDisplay
                  datetime={DateTime.fromSQL(row.applicationDate)}
                />
              ),
          },
          {
            text: 'Subtracking',
            field: 'subtracking',
          },
        ]}
        data={has(report, 'data.applications') && report.data.applications}
        isLoading={report.status === 'fetching'}
        onSortChange={changeSort}
        sort={sort}
        pagination={{
          total: get(report, 'data.pagination.total'),
          pageSize: get(report, 'data.pagination.pageSize'),
          currentPage: get(report, 'data.pagination.currentPage'),
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
        }}
      />
    </>
  )
}

export default ApplicationsTable
